import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ResolvedElement } from "@/lib/visibility-types";

type ResolvedOrNone =
	| ResolvedElement
	| {
			elementId: string;
			effective: false;
			source: "none";
			grantedByRole: "";
			grantedByDisplay: null;
			depth: 0;
	  };

interface VisibilitySourceCardProps {
	resolved: ResolvedOrNone;
	currentRole: string;
	isPinned?: boolean;
	/** Page not built yet — show a notice instead of the enable button. */
	isLocked?: boolean;
	onSwitchRole: (role: string) => void;
	onEnableForCurrent: (elementId: string) => void;
	getRoleLabel: (slug: string) => string;
}

// Tokeny semantyczne z globals.css — bez hardcoded palette ("bg-blue-500/X" lamie frontend/AGENTS.md).
const ACCENT_BY_SOURCE: Record<string, string> = {
	own: "bg-primary",
	inherited: "bg-info",
	blockedHere: "bg-destructive",
	blockedInherited: "bg-destructive/60",
	none: "bg-muted-foreground/40",
};

export function VisibilitySourceCard({
	resolved,
	isPinned = false,
	isLocked = false,
	onSwitchRole,
	onEnableForCurrent,
	getRoleLabel,
}: VisibilitySourceCardProps) {
	const { source, grantedByRole, grantedByDisplay, elementId, depth } = resolved;
	const fallbackItalic = grantedByDisplay === null && grantedByRole !== "";

	const renderRoleName = () =>
		fallbackItalic ? (
			<span className="italic text-muted-foreground">{grantedByRole} (brak nazwy)</span>
		) : (
			<span className="font-medium">{getRoleLabel(grantedByRole)}</span>
		);

	// Always use a generic button label — avoids duplicate text matches in RTL queries
	// (the role name is already shown in renderRoleName() above the button).
	const switchLabel = "tę rolę";

	return (
		<div className="relative pl-3">
			<div
				className={cn(
					"absolute left-0 top-0 h-full w-[2px] rounded-full",
					ACCENT_BY_SOURCE[source],
				)}
			/>
			{source === "own" && (
				<div>
					<div className="text-sm font-medium">Reguła ustawiona tutaj</div>
					<div className="mt-1 text-xs text-muted-foreground">Aktualna rola ma własny wpis</div>
				</div>
			)}
			{source === "inherited" && (
				<div>
					<div className="text-sm font-medium">Dziedziczone</div>
					<div className="mt-1 text-xs text-muted-foreground">
						Z roli: {renderRoleName()}
						<div className="mt-0.5">Głębokość w łańcuchu: {depth}</div>
					</div>
					<Button
						type="button"
						size="sm"
						variant="outline"
						className="mt-2 h-7 text-xs"
						onClick={() => onSwitchRole(grantedByRole)}
					>
						Przełącz na: {switchLabel}
					</Button>
				</div>
			)}
			{source === "blockedHere" && (
				<div>
					<div className="text-sm font-medium">Wyłączone tutaj</div>
					<div className="mt-1 text-xs text-muted-foreground">Aktualna rola blokuje element</div>
				</div>
			)}
			{source === "blockedInherited" && (
				<div>
					<div className="text-sm font-medium">Zablokowane</div>
					<div className="mt-1 text-xs text-muted-foreground">Przez rolę: {renderRoleName()}</div>
					<Button
						type="button"
						size="sm"
						variant="outline"
						className="mt-2 h-7 text-xs"
						onClick={() => onSwitchRole(grantedByRole)}
					>
						Przełącz na: {switchLabel}
					</Button>
				</div>
			)}
			{source === "none" && (
				<div>
					<div className="text-sm font-medium">Brak reguły</div>
					<div className="mt-1 text-xs text-muted-foreground">Domyślnie ukryte</div>
					{isLocked ? (
						<div className="mt-2 text-xs italic text-status-warning-text">
							Strona w przygotowaniu — nie można jeszcze włączyć
						</div>
					) : isPinned ? (
						<div className="mt-2 text-xs italic text-muted-foreground">
							Element przypięty — visible=false zablokowane przez backend
						</div>
					) : (
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="mt-2 h-7 text-xs"
							onClick={() => onEnableForCurrent(elementId)}
						>
							Włącz dla aktualnej roli
						</Button>
					)}
				</div>
			)}
		</div>
	);
}
